import type { UserResource } from '@clerk/shared/types';
import { isInternalTeamMemberByEmail } from '@this+that/utils';

import { pushGTMEvent } from '../analytics/gtm';

export type EmailVolumeBucket = '<50' | '50-149' | '150-299' | '300+';

export type Region =
  | 'Americas'
  | 'Europe'
  | 'Asia'
  | 'Africa'
  | 'Oceania'
  | 'Unknown';

export interface GTMUserProperties {
  event: 'user_properties_set';
  user_id: string;
  team_count?: number;
  job_title?: string;
  email_volume_bucket_daily?: EmailVolumeBucket;
  region?: Region;
  timezone?: string;
  is_internal_user: boolean;
}

export function getRegionFromTimezone(timezone?: string): Region {
  if (!timezone) return 'Unknown';

  const prefix = timezone.split('/')[0].toLowerCase();

  const regionMap: Record<string, Region> = {
    america: 'Americas',
    europe: 'Europe',
    asia: 'Asia',
    africa: 'Africa',
    australia: 'Oceania',
    pacific: 'Oceania',
    atlantic: 'Europe',
    indian: 'Asia',
    etc: 'Unknown',
  };

  return regionMap[prefix] || 'Unknown';
}

export function calculateEmailVolumeBucket(
  stats?: Array<{ userCommunications?: number; statTimestamp?: number }>,
): EmailVolumeBucket {
  if (!stats || stats.length === 0) return '<50';

  const validStats = stats.filter(
    (stat) =>
      stat.userCommunications !== undefined && stat.userCommunications > 0,
  );

  if (validStats.length === 0) return '<50';

  const totalEmails = validStats.reduce(
    (sum, stat) => sum + (stat.userCommunications || 0),
    0,
  );
  const dailyAverage = totalEmails / validStats.length;

  if (dailyAverage < 50) return '<50';
  if (dailyAverage < 150) return '50-149';
  if (dailyAverage < 300) return '150-299';
  return '300+';
}

export function setGTMUserProperties(
  user: UserResource,
  emailBucket: EmailVolumeBucket | undefined,
  teamCount: number | undefined,
): void {
  const timezone = user.unsafeMetadata?.ht_timezone as string | undefined;
  const jobTitle = user.unsafeMetadata?.ht_title as string | undefined;
  const region = getRegionFromTimezone(timezone);
  const primaryEmail = user.emailAddresses[0]?.emailAddress;
  const is_internal_user = primaryEmail
    ? isInternalTeamMemberByEmail(primaryEmail)
    : false;

  const userProperties: GTMUserProperties = {
    event: 'user_properties_set',
    user_id: primaryEmail || user.id,
    team_count: teamCount,
    job_title: jobTitle,
    email_volume_bucket_daily: emailBucket,
    region,
    timezone,
    is_internal_user,
  };

  pushGTMEvent(userProperties);
}

export interface GTMActiveTeamEvent {
  event: 'active_team_changed';
  active_team_id: string;
}

// Pushed on every active-team switch so subsequent analytics events are
// attributable to the current team (per-team usage). Separate from the
// once-per-session user_properties_set event.
export function setGTMActiveTeam(activeTeamId: string): void {
  const activeTeamEvent: GTMActiveTeamEvent = {
    event: 'active_team_changed',
    active_team_id: activeTeamId,
  };

  pushGTMEvent(activeTeamEvent);
}
